import { Check } from "lucide-react";
import { type KeyboardEvent, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils";

export interface CellOption {
  value: string;
  label: string;
}

/** `selected` holds option values; `text` is typed text (the whole answer when there are no options). */
export interface CellEditorValue {
  selected: string[];
  text: string;
}

/** `submit` is Cmd/Ctrl+Enter: commit, then let the row save. */
export type CellEditorCommitVia = "enter" | "shift-enter" | "tab" | "shift-tab" | "submit" | "click" | "blur";

/** Prefix matches first, then word-start, then anywhere; catalog order within each rank. */
export function rankOptions<T extends CellOption>(options: readonly T[], query: string): T[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...options];
  const rank = (label: string) => {
    const haystack = label.toLowerCase();
    if (haystack.startsWith(needle)) return 0;
    if (haystack.split(/[\s\-/(),]+/).some((word) => word.startsWith(needle))) return 1;
    return haystack.includes(needle) ? 2 : 3;
  };
  return options
    .map((option, index) => ({ option, index, rank: rank(option.label) }))
    .filter((entry) => entry.rank < 3)
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((entry) => entry.option);
}

const FREE_TEXT = "\u0000free-text";

/**
 * A grid cell's editor: one text input that owns focus for the whole edit. With
 * `options` it is a combobox whose list points at the highlighted option through
 * aria-activedescendant, so no popup ever takes focus and nothing has to hand it
 * back to the grid. Keys it handles are stopped in the capture phase, before the
 * grid's own listener can treat them as navigation.
 */
export function CellEditor({
  label,
  options,
  multiple = false,
  freeText = false,
  selected = [],
  text = "",
  initialText,
  suffix,
  inputMode,
  className,
  onCommit,
  onCancel,
}: {
  label: string;
  options?: readonly CellOption[];
  multiple?: boolean;
  /** Typed text that matches no option may be committed as `text`. */
  freeText?: boolean;
  selected?: readonly string[];
  text?: string;
  /** The keystroke that opened the editor; replaces the current text. */
  initialText?: string;
  suffix?: string;
  inputMode?: "text" | "decimal" | "numeric";
  className?: string;
  /** Return false to refuse the value and keep editing. */
  onCommit: (value: CellEditorValue, via: CellEditorCommitVia) => unknown;
  onCancel: () => void;
}) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const finished = useRef(false);
  const listed = options !== undefined;
  const labelOf = (value: string) => options?.find((option) => option.value === value)?.label ?? value;
  const [chosen, setChosen] = useState<string[]>(() => [...selected]);
  const [freeAnswer, setFreeAnswer] = useState(text);
  const [query, setQuery] = useState(() => {
    if (initialText !== undefined) return initialText;
    if (!listed) return text;
    if (multiple) return "";
    return selected[0] ? labelOf(selected[0]) : text;
  });
  const [dirty, setDirty] = useState(initialText !== undefined);
  const [open, setOpen] = useState(listed);
  const [invalid, setInvalid] = useState(false);

  const filtering = multiple || dirty;
  const ranked = listed ? (filtering ? rankOptions(options, query) : [...options]) : [];
  const typed = query.trim();
  const items: CellOption[] =
    freeText && typed && !options?.some((option) => option.label.toLowerCase() === typed.toLowerCase())
      ? [...ranked, { value: FREE_TEXT, label: `Other: ${typed}` }]
      : ranked;
  const [active, setActive] = useState(() => {
    if (filtering) return typed ? 0 : -1;
    return items.findIndex((item) => item.value === selected[0]);
  });
  const highlighted = open || !multiple ? items[active] : undefined;

  useLayoutEffect(() => {
    // Caret at the end, as in Sheets; replacing a value is what typing on the unopened cell does.
    const end = input.current?.value.length ?? 0;
    input.current?.focus();
    input.current?.setSelectionRange(end, end);
  }, []);

  function commit(value: CellEditorValue, via: CellEditorCommitVia) {
    if (finished.current) return;
    const accepted = onCommit(value, via) !== false;
    finished.current = accepted;
    setInvalid(!accepted);
  }

  function toggle(item: CellOption) {
    const next =
      item.value === FREE_TEXT
        ? chosen
        : chosen.includes(item.value)
          ? chosen.filter((value) => value !== item.value)
          : [...chosen, item.value];
    const nextText = item.value === FREE_TEXT ? typed : freeAnswer;
    setChosen(next);
    setFreeAnswer(nextText);
    setQuery("");
    setActive(-1);
    return { selected: next, text: nextText };
  }

  /** What the editor holds right now, or undefined when typed text matches nothing it may keep. */
  function resolve(): CellEditorValue | undefined {
    if (!listed) return { selected: [], text: query };
    if (multiple) return { selected: chosen, text: freeAnswer };
    if (highlighted) {
      return highlighted.value === FREE_TEXT
        ? { selected: [], text: typed }
        : { selected: [highlighted.value], text: "" };
    }
    if (!dirty) return { selected: [...selected], text };
    if (!typed) return { selected: [], text: "" };
    return undefined;
  }

  function finish(via: CellEditorCommitVia) {
    const value = resolve();
    if (value) commit(value, via);
    else setInvalid(true);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.nativeEvent.isComposing) return;
    const owned = () => {
      event.preventDefault();
      event.stopPropagation();
    };
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp": {
        if (!listed || event.altKey || event.metaKey || event.ctrlKey) return;
        owned();
        if (!open) {
          setOpen(true);
          return;
        }
        if (!items.length) return;
        const step = event.key === "ArrowDown" ? 1 : -1;
        setActive((current) => (current + step + items.length + (current < 0 && step < 0 ? 1 : 0)) % items.length);
        return;
      }
      case "Enter": {
        owned();
        if (event.metaKey || event.ctrlKey) {
          finish("submit");
          return;
        }
        if (multiple && highlighted) {
          toggle(highlighted);
          return;
        }
        finish(event.shiftKey ? "shift-enter" : "enter");
        return;
      }
      case "Tab": {
        owned();
        const via = event.shiftKey ? "shift-tab" : "tab";
        if (multiple && highlighted && typed) commit(toggle(highlighted), via);
        else finish(via);
        return;
      }
      case "Escape": {
        owned();
        if (listed && open) setOpen(false);
        else {
          finished.current = true;
          onCancel();
        }
        return;
      }
      case "Backspace": {
        if (multiple && !query && chosen.length) {
          owned();
          setChosen(chosen.slice(0, -1));
        }
        return;
      }
    }
  }

  const listId = `${id}-list`;
  const optionId = (index: number) => `${id}-option-${index}`;

  return (
    <div
      className={cn(
        // The Sheets look: no field chrome, just the focused cell's own 1px border becoming editable.
        "flex min-w-0 items-center gap-1 overflow-hidden bg-background text-foreground outline-1 outline-solid outline-primary -outline-offset-1",
        invalid && "outline-destructive",
        className,
      )}
    >
      {multiple &&
        chosen.map((value) => (
          <span key={value} className="shrink-0 rounded-sm bg-muted px-1 text-xs">
            {labelOf(value)}
          </span>
        ))}
      {multiple && freeAnswer && <span className="rounded-sm bg-muted px-1 text-xs">{freeAnswer}</span>}
      <input
        ref={input}
        aria-label={label}
        {...(listed && {
          role: "combobox",
          "aria-expanded": open,
          "aria-controls": open ? listId : undefined,
          "aria-autocomplete": "list" as const,
          "aria-activedescendant": open && highlighted ? optionId(active) : undefined,
        })}
        aria-invalid={invalid || undefined}
        inputMode={inputMode}
        autoComplete="off"
        spellCheck={false}
        className="h-full min-w-0 flex-1 bg-transparent p-0 outline-none [font:inherit]"
        value={query}
        onChange={(event) => {
          let next = event.target.value;
          // The untouched answer behaves like a placeholder: the first keystroke replaces it, Backspace clears it.
          if (listed && !multiple && !dirty && query) {
            if (next.startsWith(query)) next = next.slice(query.length);
            else if (query.startsWith(next)) next = "";
          }
          setQuery(next);
          setDirty(true);
          setOpen(listed);
          setInvalid(false);
          setActive(next.trim() ? 0 : -1);
        }}
        onKeyDownCapture={onKeyDown}
        onBlur={() => {
          if (finished.current) return;
          // Focus has already left, so a value that cannot be kept is dropped rather than left half-edited.
          const value = resolve();
          finished.current = true;
          if (!value || onCommit(value, "blur") === false) onCancel();
        }}
      />
      {suffix && <span className="shrink-0 text-xs text-muted-foreground">{suffix}</span>}
      {listed && open && (
        <OptionList
          anchor={input}
          id={listId}
          label={label}
          items={items}
          active={active}
          optionId={optionId}
          checked={multiple ? (value) => chosen.includes(value) || (value === FREE_TEXT && !!freeAnswer) : undefined}
          onChoose={(item) => {
            if (multiple) toggle(item);
            else
              commit(
                item.value === FREE_TEXT ? { selected: [], text: typed } : { selected: [item.value], text: "" },
                "click",
              );
          }}
        />
      )}
    </div>
  );
}

/** Portaled to the body so a clipping card or scrolling grid never cuts it off. */
function OptionList({
  anchor,
  id,
  label,
  items,
  active,
  optionId,
  checked,
  onChoose,
}: {
  anchor: React.RefObject<HTMLInputElement | null>;
  id: string;
  label: string;
  items: readonly CellOption[];
  active: number;
  optionId: (index: number) => string;
  checked?: (value: string) => boolean;
  onChoose: (item: CellOption) => void;
}) {
  const list = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<{ top?: number; bottom?: number; left: number; minWidth: number }>();

  useLayoutEffect(() => {
    const field = anchor.current?.parentElement;
    if (!field) return;
    const update = () => {
      const rect = field.getBoundingClientRect();
      const gap = 4;
      // A fixed max height keeps filtering from flipping the list across the input.
      const below = rect.bottom + gap + 240 <= window.innerHeight;
      setPlace({
        ...(below ? { top: rect.bottom + gap } : { bottom: window.innerHeight - rect.top + gap }),
        left: rect.left,
        minWidth: Math.max(rect.width, 192),
      });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [anchor]);

  useLayoutEffect(() => {
    if (active < 0) return;
    list.current?.querySelector(`#${CSS.escape(optionId(active))}`)?.scrollIntoView?.({ block: "nearest" });
  }, [active, optionId]);

  return createPortal(
    <div
      ref={list}
      role="listbox"
      id={id}
      aria-label={label}
      aria-multiselectable={checked ? true : undefined}
      style={{ position: "fixed", zIndex: 50, ...place }}
      className="max-h-60 max-w-sm overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground text-sm shadow-md"
    >
      {items.length === 0 && <div className="px-2 py-1.5 text-muted-foreground">No matches</div>}
      {items.map((item, index) => (
        // biome-ignore lint/a11y/useFocusableInteractive: the input owns focus through aria-activedescendant
        // biome-ignore lint/a11y/useKeyWithClickEvents: the input handles every key for the list
        <div
          key={item.value}
          role="option"
          id={optionId(index)}
          aria-selected={index === active}
          aria-checked={checked ? checked(item.value) : undefined}
          className={cn(
            "flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5",
            index === active && "bg-accent text-accent-foreground",
          )}
          // Keeps focus, and so the edit, in the input.
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onChoose(item)}
        >
          {checked && <Check className={cn("size-3.5 shrink-0", !checked(item.value) && "invisible")} />}
          {item.label}
        </div>
      ))}
    </div>,
    document.body,
  );
}
